import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { featureFlagsApi } from '@/api/featureFlags'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/PageHeader'
import { copy } from '@/lib/copy'
import { FEATURE_FLAG_META } from '@/config/featureFlagMeta'
import { getApiErrorMessage } from '@/lib/apiError'
import { cn } from '@/components/ui/cn'
import type { FeatureFlag } from '@/types/featureFlag'

const FLAG_GROUPS: { title: string; keys: string[] }[] = [
  { title: copy.admin.flagsGroupIntelligence, keys: ['learning_intelligence', 'language_unit_resolver', 'grammar_pattern_matcher', 'grammar_targeted_practice'] },
  { title: copy.admin.flagsGroupContent, keys: ['content_scoring', 'explainability'] },
  { title: copy.admin.flagsGroupMonetization, keys: ['premium'] },
]

function MetaList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) {
    return null
  }
  return (
    <div>
      <p className="text-xs font-semibold text-foreground">{title}</p>
      <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs leading-relaxed text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

function FlagRow({ flag }: { flag: FeatureFlag }) {
  const queryClient = useQueryClient()
  const meta = FEATURE_FLAG_META[flag.key]

  const updateMutation = useMutation({
    mutationFn: (payload: { enabled: boolean; rolloutPercentage: number }) =>
      featureFlagsApi.update(flag.key, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] })
    },
  })

  const handleToggle = () => {
    updateMutation.mutate({
      enabled: !flag.enabled,
      rolloutPercentage: flag.rolloutPercentage,
    })
  }

  const handleRolloutChange = (value: number) => {
    updateMutation.mutate({
      enabled: flag.enabled,
      rolloutPercentage: value,
    })
  }

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-2 py-0.5 text-xs font-medium">{flag.key}</code>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  flag.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                {flag.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <span className="text-xs text-muted-foreground">
                Rollout: {flag.rolloutPercentage}%
              </span>
            </div>
            {flag.description ? (
              <p className="mt-2 text-sm text-muted-foreground">{flag.description}</p>
            ) : null}
            <p className="mt-1 text-xs text-muted-foreground/70">
              Updated {new Date(flag.updatedAt).toLocaleString()}
            </p>
          </div>

          {meta ? (
            <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <MetaList title={copy.admin.flagsBackend} items={meta.backend} />
                <MetaList title={copy.admin.flagsFrontend} items={meta.frontend} />
              </div>
              <MetaList title={copy.admin.flagsWhenEnabled} items={meta.whenEnabled} />
              <MetaList title={copy.admin.flagsWhenDisabled} items={meta.whenDisabled} />
              <MetaList title={copy.admin.flagsRolloutBehavior} items={meta.rollout} />
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{copy.admin.flagsDefaultState}:</span>{' '}
                {meta.defaultState}
              </p>
              {meta.notes ? <MetaList title={copy.admin.flagsNotes} items={meta.notes} /> : null}
            </div>
          ) : null}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-3 lg:w-56">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-muted-foreground">Enabled</span>
            <button
              type="button"
              role="switch"
              aria-checked={flag.enabled}
              aria-label={`Toggle ${flag.key}`}
              onClick={handleToggle}
              disabled={updateMutation.isPending}
              className={cn(
                'relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors',
                flag.enabled ? 'bg-primary' : 'bg-muted',
                updateMutation.isPending && 'opacity-60',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform',
                  flag.enabled ? 'translate-x-5' : 'translate-x-0',
                )}
              />
            </button>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
              <span>Rollout</span>
              <span>{flag.rolloutPercentage}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={flag.rolloutPercentage}
              disabled={!flag.enabled || updateMutation.isPending}
              onChange={(e) => handleRolloutChange(Number(e.target.value))}
              className="w-full accent-primary disabled:opacity-50"
            />
            <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground/80">
              {!flag.enabled
                ? 'Enable the flag first to adjust rollout.'
                : flag.rolloutPercentage === 100
                  ? 'All non-admin users included.'
                  : flag.rolloutPercentage === 0
                    ? 'Only admins (when enabled).'
                    : `~${flag.rolloutPercentage}% of users (stable hash).`}
            </p>
          </div>
        </div>
      </div>

      {updateMutation.isError ? (
        <p className="mt-2 text-xs text-destructive">
          {getApiErrorMessage(updateMutation.error, copy.admin.flagsUpdateFailed)}
        </p>
      ) : null}
    </Card>
  )
}

export function AdminFeatureFlagsPage() {
  const flagsQuery = useQuery({
    queryKey: ['admin-feature-flags'],
    queryFn: () => featureFlagsApi.list(),
  })

  const flagsByKey = new Map((flagsQuery.data ?? []).map((flag) => [flag.key, flag]))

  return (
    <div>
      <PageHeader title={copy.admin.flagsTitle} description={copy.admin.flagsDescription} />

      <div className="mb-4">
        <Link
          to="/admin"
          className="inline-flex h-9 items-center gap-1 rounded-md bg-secondary px-3 text-xs text-secondary-foreground hover:bg-secondary/80"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {copy.admin.backToAdmin}
        </Link>
      </div>

      <Card className="mb-6 p-4">
        <h2 className="text-sm font-semibold">{copy.admin.flagsHowItWorksTitle}</h2>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-muted-foreground">
          {copy.admin.flagsHowItWorks.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </Card>

      {flagsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{copy.admin.flagsLoading}</p>
      ) : flagsQuery.isError ? (
        <p className="text-sm text-destructive">
          {getApiErrorMessage(flagsQuery.error, copy.admin.flagsLoadFailed)}
        </p>
      ) : (
        <div className="space-y-8">
          {FLAG_GROUPS.map((group) => {
            const groupFlags = group.keys
              .map((key) => flagsByKey.get(key))
              .filter((flag): flag is FeatureFlag => flag != null)
            if (groupFlags.length === 0) {
              return null
            }
            return (
              <section key={group.title}>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.title}
                </h2>
                <div className="space-y-3">
                  {groupFlags.map((flag) => (
                    <FlagRow key={flag.key} flag={flag} />
                  ))}
                </div>
              </section>
            )
          })}
          {(() => {
            const groupedKeys = new Set(FLAG_GROUPS.flatMap((g) => g.keys))
            const ungrouped = (flagsQuery.data ?? []).filter((f) => !groupedKeys.has(f.key))
            if (ungrouped.length === 0) {
              return null
            }
            return (
              <section>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {copy.admin.flagsGroupOther}
                </h2>
                <div className="space-y-3">
                  {ungrouped.map((flag) => (
                    <FlagRow key={flag.key} flag={flag} />
                  ))}
                </div>
              </section>
            )
          })()}
        </div>
      )}
    </div>
  )
}
